import {
  useEffect,
  useState,
  type FormEvent,
} from 'react'
import {
  Eye,
  EyeOff,
} from 'lucide-react'
import {
  PairingModalShell,
} from './pairing/PairingModalShell'
import {
  authService,
  getUserFacingApiErrorMessage,
} from '../services/api'

const INPUT_CLASS =
  'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'

const CONNECT_FAILED =
  "We couldn't connect that inbox just now. Please try again in a moment."

// The backend's 422 when the outgoing server is unknown (routers/connected_email.py).
const SMTP_REQUIRED = 'Enter the outgoing mail server.'

const BILLING_FAILED =
  "We couldn't set up the add-on billing, so nothing was connected and nothing was charged. Please try again."

const APPLE_APP_PASSWORD_HELP = 'https://support.apple.com/en-us/102654'

// The mail server for the common IMAP providers, by address domain, so most
// people never see a server field they would have to look up.
const YAHOO_DOMAINS = [
  'yahoo.com', 'ymail.com', 'rocketmail.com', 'yahoo.co.uk', 'yahoo.ca',
  'yahoo.fr', 'yahoo.de', 'yahoo.it', 'yahoo.es', 'yahoo.com.au',
  'yahoo.co.in', 'yahoo.in', 'yahoo.com.br', 'yahoo.com.mx', 'yahoo.co.nz',
  'yahoo.ie', 'yahoo.com.sg',
]
// yahoo.co.jp is a separate service with its own servers, so it is left out.
const IMAP_HOST_BY_DOMAIN: Record<string, string> = {
  'icloud.com': 'imap.mail.me.com',
  'me.com': 'imap.mail.me.com',
  'mac.com': 'imap.mail.me.com',
  ...Object.fromEntries(YAHOO_DOMAINS.map((domain) => [domain, 'imap.mail.yahoo.com'])),
  'aol.com': 'imap.aol.com',
  'aim.com': 'imap.aol.com',
  'verizon.net': 'imap.aol.com',
  'fastmail.com': 'imap.fastmail.com',
  'fastmail.fm': 'imap.fastmail.com',
}

// The outgoing server of the providers above, mirrored from the backend's
// SMTP_FOR_IMAP_HOST: for these the form never asks.
const SMTP_BY_IMAP_HOST: Record<string, string> = {
  'imap.mail.me.com': 'smtp.mail.me.com',
  'imap.mail.yahoo.com': 'smtp.mail.yahoo.com',
  'imap.aol.com': 'smtp.aol.com',
  'imap.fastmail.com': 'smtp.fastmail.com',
}

// Hosts where a custom-domain address signs in under another name (iCloud+:
// the @icloud.com address; Fastmail: the account's login address).
const SEPARATE_LOGIN_HOSTS = new Set(['imap.mail.me.com', 'imap.fastmail.com'])

export const imapHostForEmail = (email: string): string | null => {
  const domain = email.trim().toLowerCase().split('@')[1] ?? ''
  return IMAP_HOST_BY_DOMAIN[domain] ?? null
}

const isAppleAddress = (email: string): boolean =>
  imapHostForEmail(email) === 'imap.mail.me.com'

interface ImapConnectModalProps {
  isOpen: boolean
  onClose: () => void
  onConnected: () => void
  /**
   * A reconnect: the inbox whose password is re-entered. Its stored server is
   * reused, so only the password is asked for.
   */
  reconnect?: { accountId: number; email: string } | null
}

export function ImapConnectModal({
  isOpen,
  onClose,
  onConnected,
  reconnect = null,
}: ImapConnectModalProps): JSX.Element {
  const initialEmail = reconnect?.email ?? ''
  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [revealed, setRevealed] = useState(false)
  // Shown only when the address is not one we know the server for, or the
  // user asks to change it.
  const [host, setHost] = useState('')
  const [showServer, setShowServer] = useState(false)
  const [login, setLogin] = useState('')
  // Only asked for when the server is one we don't know (custom domains).
  const [smtpHost, setSmtpHost] = useState('')
  // A reconnect of a custom-domain inbox saved before sending existed: the
  // server answers that it needs the outgoing server, so the form asks.
  const [reconnectNeedsSmtp, setReconnectNeedsSmtp] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setEmail(initialEmail)
    setPassword('')
    setRevealed(false)
    setHost('')
    setLogin('')
    setSmtpHost('')
    setReconnectNeedsSmtp(false)
    setShowServer(false)
    setError(null)
  }, [isOpen, initialEmail])

  const knownHost = imapHostForEmail(email)
  const needsServer = !reconnect && email.includes('@') && knownHost === null
  const effectiveHost = (showServer || needsServer ? host : knownHost ?? host).trim()
  // Only a custom domain on iCloud/Fastmail signs in under a different name.
  const offersLogin =
    !reconnect && (showServer || needsServer)
    && SEPARATE_LOGIN_HOSTS.has(effectiveHost.toLowerCase())
  const needsSmtp = reconnect
    ? reconnectNeedsSmtp
    : Boolean(effectiveHost) && !(effectiveHost.toLowerCase() in SMTP_BY_IMAP_HOST)
  const canSubmit =
    Boolean(
      email.trim() && password && (reconnect || effectiveHost)
      && (!needsSmtp || smtpHost.trim()),
    ) && !submitting

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const status = await authService.connectImapInbox({
        email: email.trim(),
        password,
        ...(reconnect
          ? { account_id: reconnect.accountId }
          : { host: effectiveHost }),
        ...(offersLogin && login.trim() ? { login: login.trim() } : {}),
        ...(needsSmtp ? { smtp_host: smtpHost.trim() } : {}),
      })
      if (status === 'billing_error') {
        setError(BILLING_FAILED)
        return
      }
      onConnected()
    } catch (err) {
      const message = getUserFacingApiErrorMessage(err, CONNECT_FAILED)
      if (reconnect && message === SMTP_REQUIRED) {
        setReconnectNeedsSmtp(true)
      }
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const smtpField = (
    <>
                <label
                  htmlFor="imap-smtp-host"
                  className="mt-3 block text-sm font-medium text-gray-700"
                >
                  Outgoing mail server (SMTP)
                </label>
                <input
                  id="imap-smtp-host"
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  value={smtpHost}
                  onChange={(e) => { setSmtpHost(e.target.value); setError(null) }}
                  placeholder="smtp.example.com"
                  className={`${INPUT_CLASS} font-mono`}
                  disabled={submitting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Connects over SSL on port 465.
                </p>
              </>
  )

  return (
    <PairingModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={reconnect ? 'Reconnect your inbox' : 'Connect an iCloud or other inbox'}
      closeAriaLabel="Close"
    >
      <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
        <p className="text-sm text-gray-600">
          Works with iCloud, Yahoo, Fastmail and any inbox that supports IMAP.
          Your assistant can search it, tidy it, draft replies and, with your
          approval, send from it. If your provider has a calendar, as iCloud
          does, your assistant can manage it too.
        </p>

        <div>
          <label htmlFor="imap-email" className="block text-sm font-medium text-gray-700">
            Email address
          </label>
          <input
            id="imap-email"
            type="email"
            autoComplete="off"
            spellCheck={false}
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(null) }}
            placeholder="you@icloud.com"
            className={INPUT_CLASS}
            disabled={submitting || Boolean(reconnect)}
          />
        </div>

        <div>
          <label htmlFor="imap-password" className="block text-sm font-medium text-gray-700">
            App-specific password
          </label>
          <div className="relative">
            <input
              id="imap-password"
              type={revealed ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(null) }}
              className={`${INPUT_CLASS} pr-10`}
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setRevealed((r) => !r)}
              aria-label={revealed ? 'Hide password' : 'Show password'}
              className="absolute inset-y-0 right-0 mt-1 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            >
              {revealed
                ? <Eye className="h-5 w-5" aria-hidden="true" />
                : <EyeOff className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Not your account password: create one just for Catch in your email
            provider's security settings.{' '}
            {isAppleAddress(email) && (
              <a
                href={APPLE_APP_PASSWORD_HELP}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700"
              >
                How to create one for iCloud
              </a>
            )}
          </p>
        </div>

        {reconnect ? (needsSmtp && <div>{smtpField}</div>) : showServer || needsServer ? (
          <div>
            <label htmlFor="imap-host" className="block text-sm font-medium text-gray-700">
              Incoming mail server (IMAP)
            </label>
            <input
              id="imap-host"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={host}
              onChange={(e) => { setHost(e.target.value); setError(null) }}
              placeholder={knownHost ?? 'imap.example.com'}
              className={`${INPUT_CLASS} font-mono`}
              disabled={submitting}
            />
            <p className="mt-1 text-xs text-gray-500">
              Connects over SSL on port 993.
            </p>
            {needsSmtp && smtpField}
          </div>
        ) : (
          knownHost && (
            <button
              type="button"
              onClick={() => {
                setHost(knownHost)
                setShowServer(true)
              }}
              className="text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              Using {knownHost} · Change server
            </button>
          )
        )}

        {offersLogin && (
          <div>
            <label htmlFor="imap-login" className="block text-sm font-medium text-gray-700">
              Sign-in name (optional)
            </label>
            <input
              id="imap-login"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={login}
              onChange={(e) => { setLogin(e.target.value); setError(null) }}
              placeholder="you@icloud.com"
              className={INPUT_CLASS}
              disabled={submitting}
            />
            <p className="mt-1 text-xs text-gray-500">
              For a custom-domain address, the address you sign in to your
              provider with.
            </p>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            disabled={!canSubmit}
          >
            {submitting ? 'Checking sign-in…' : 'Connect'}
          </button>
        </div>
      </form>
    </PairingModalShell>
  )
}
